'use client';

import {
  ControlType,
  CalculatorId,
  CalculatorType,
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type SingleColumnSelectControl,
  type ControlValues
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { CoefficientCalculator } from './coefficient-of-variation-calculator';
import { calculateCoefficientOfVariation, calculateMean, calculateStandardDeviation } from '../../../lib/stats';

interface CoefficientControlValues extends ControlValues {
  [ControlIds.DataColumn]: string[];
}

const dataColumn: SingleColumnSelectControl = {
  id: ControlIds.DataColumn,
  type: ControlType.SingleColumnSelect,
  label: 'Data column',
  required: true,
  numericOnly: true
};

export const coefficientControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [dataColumn] as const
    }
  ]
};

export const coefficientConfig: CalculatorConfig<CalculatorId.COEFFICIENT_OF_VARIATION> = {
  id: CalculatorId.COEFFICIENT_OF_VARIATION,
  type: CalculatorType.Graph,
  name: 'Coefficient of variation',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'returns', 
        name: 'Investment Returns (%)', 
        values: ['12.5', '15.2', '10.8', '14.3', '11.7', '13.9', '12.1', '16.4', '13.5', '14.8']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.DataColumn]: 'returns'
  },
  controls: coefficientControls,
  getInitialConfig: (data: TableDataJSON) => {
    // Find the first numeric column
    const numericColumn = data.columns.find(col => 
      col.values.every(val => !isNaN(Number(val)))
    )?.id;

    return {
      [ControlIds.DataColumn]: numericColumn || ''
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const dataColumn = controls[ControlIds.DataColumn] as string;
    if (!dataColumn) return null;

    const col = data.columns.find(col => col.id === dataColumn);
    if (!col) return null;
    const numericData = col.values.map(Number);
    if (numericData.some(isNaN)) return null;

    const cv = calculateCoefficientOfVariation({ data: numericData });
    const mean = calculateMean({ data: numericData });
    const standardDeviation = calculateStandardDeviation({ data: numericData });

    return {
      cv,
      mean,
      standardDeviation,
      data: numericData
    };
  },
  render: ({ visualizationData }) => <CoefficientCalculator visualizationData={visualizationData} />
}; 